import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CarServices from '../../services/CarServices';
import CategoryServices from '../../services/CategoryServices';
import ReactQuill from 'react-quill';

function ServiceUpdate({ service, closeModal, onSuccess }) {
    const [formValues, setFormValues] = useState({
        terms: '',
        category: '',
        subCategory: '',
        title: '',
        description: '',
        images: [],
    });

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await CategoryServices.getCategoryActive();
                if (res.status) {
                    setCategories(res.data);
                } else {
                    alert('Failed to fetch categories');
                }
            } catch (error) {
                console.error('Error fetching categories', error);
                alert('Failed to fetch categories');
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (service) {
            const initialSubCategory = service?.subCategory?._id || '';
            setFormValues({
                category: service?.category?._id || '',
                subCategory: initialSubCategory,
                title: service?.title || '',
                description: service?.description || '',
                terms: service?.terms || '',
                images: service?.images || [],
            });
            // Set previews for existing images
            if (service.images && service.images.length > 0) {
                setPreviewImages(service.images);
            }
            // Fetch subcategories for the initial category
            if (service?.category?._id) {
                fetchSubcategories(service.category._id);
            }
        }
    }, [service]);

    const fetchSubcategories = async (categoryId) => {
        try {
            const res = await CategoryServices.getSubCategoryByCategory(categoryId);
            if (res.status && Array.isArray(res.data)) {
                setSubCategories(res.data);
            } else {
                setSubCategories([]);
            }
        } catch (error) {
            console.error('Error fetching subcategories', error);
            setSubCategories([]);
        }
    };

    const handleQuillChange = (name, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    };

    const handleInputChange = async (event) => {
        const { name, value } = event.target;

        if (name === 'category') {
            setFormValues(prev => ({
                ...prev,
                category: value,
                subCategory: '',
            }));

            if (value) {
                try {
                    const res = await CategoryServices.getSubCategoryByCategory(value);
                    if (res.status && Array.isArray(res.data)) {
                        setSubCategories(res.data);
                    } else {
                        setSubCategories([]);
                    }
                } catch {
                    setSubCategories([]);
                }
            } else {
                setSubCategories([]);
            }
            return;
        }

        setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        const validFormats = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
        const filteredFiles = files.filter((file) => validFormats.includes(file.type));

        if (filteredFiles.length === 0) {
            alert("Please upload valid image files (png, jpeg, jpg, webp).");
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            filteredFiles.forEach((file) => formData.append('images', file));
            const response = await CarServices.uploadImage(formData);
            if (response && Array.isArray(response.images)) {
                const uploadedImages = response.images.map((imagePath) => {
                    const normalizedPath = imagePath.replace(/\\/g, '/');
                    return `${normalizedPath}`;
                });
                setFormValues((prevValues) => ({
                    ...prevValues,
                    images: [...prevValues.images, ...uploadedImages],
                }));
                setPreviewImages((prevPreviews) => [...prevPreviews, ...uploadedImages]);
            } else {
                console.error('Unexpected response format:', response);
                throw new Error('Image upload failed: Invalid response format');
            }
        } catch (error) {
            console.error('Image upload failed', error);
            alert('Failed to upload images. Please try again.');
        } finally {
            setUploading(false);
            event.target.value = null;
        }
    };

    const handleRemoveImage = async (imageUrl) => {
        try {
            const response = await CarServices.removeImage({ url: imageUrl });
            if (response.status) {
                setFormValues((prevValues) => ({
                    ...prevValues,
                    images: prevValues.images.filter((image) => image !== imageUrl),
                }));
                setPreviewImages((prevPreviews) => prevPreviews.filter((image) => image !== imageUrl));
                alert('Image removed successfully!');
            } else {
                alert('Failed to remove image from server');
            }
        } catch (error) {
            console.error('Failed to remove image', error);
            alert('Failed to remove image');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            const payload = {
                ...formValues,
            };
            const response = await CarServices.updateService(service._id, payload);
            if (response.status) {
                alert('Service updated successfully');
                onSuccess();
                closeModal();
            } else {
                alert('Failed to update service');
            }
        } catch (error) {
            console.error('Failed to update service:', error);
            alert('Error updating service');
        }
    };

    return (
        <div className="modal fade edit-box show d-block" id="editModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Service </h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={closeModal} aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        <div className="container-box px-5">
                            <div className="container-box-inner table-reponsive-tabe">
                                <div className="page-details">
                                    <form onSubmit={handleSubmit}>
                                        <div className='row' >
                                            <div className='col-lg-6 col-sm-6' >
                                                <div className="input-field">
                                                    <label>Category*</label>
                                                    <select
                                                        name="category"
                                                        className="form-control"
                                                        value={formValues.category}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select a category</option>
                                                        {categories.map((category) => (
                                                            <option key={category?._id} value={category?._id}>
                                                                {category?.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className='col-lg-6'>
                                                <div className="input-field">
                                                    <label>Sub Category*</label>
                                                    <select
                                                        name="subCategory"
                                                        className="form-control"
                                                        value={formValues.subCategory}
                                                        onChange={handleInputChange}
                                                        disabled={!formValues.category}
                                                        required
                                                    >
                                                        <option value="">Select Sub Category</option>
                                                        {subCategories.map(sub => (
                                                            <option key={sub._id} value={sub._id}>{sub.title}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className='col-lg-12 col-sm-12' >
                                                <div className="input-field">
                                                    <label>Title</label>
                                                    <input type="text" name="title" value={formValues.title}
                                                        onChange={handleInputChange} className="form-control" required />
                                                </div>
                                            </div>

                                            <div className='col-lg-12 col-sm-12' >
                                                <div className="input-field">
                                                    <label>Description</label>
                                                    <input type="text" name="description" value={formValues.description}
                                                        onChange={handleInputChange} className="form-control" required />
                                                </div>
                                            </div>
                                            {/* Image upload */}
                                            <div className="col-lg-12">
                                                <div className="input-field">
                                                    <label>Upload Images</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        onChange={handleFileChange}
                                                        multiple
                                                    />
                                                    <p>Supported formats: png, jpeg, jpg, webp</p>
                                                    <div className="image-preview-container-service">
                                                        {previewImages.map((image, index) => (
                                                            <div key={index} className="preview-image">
                                                                <img src={process.env.REACT_APP_URL + image} alt={`Preview ${index}`} style={{ width: 100 }} />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger"
                                                                    onClick={() => handleRemoveImage(image)}
                                                                >
                                                                    X
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-12">
                                                <div className="input-field">
                                                    <label>Terms and Condition</label>
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formValues.terms}
                                                        onChange={(value) => handleQuillChange('terms', value)}
                                                        placeholder="Terms and condition"
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <button className="submit-green-btn">Submit</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default ServiceUpdate;